import React from "react";
import {Table} from "react-bootstrap";
import {Bar, BarChart, CartesianGrid, Cell, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import useAnalysisData from "./useAnalysisData.js";
import {formatEuro, formatEuroCompact} from "../Statistics/format.js";
import AnalysisCard from "./AnalysisCard.jsx";
import {currentYearLabel, formatEuroWhole, referenceLabels} from "./format.js";

const GRAFFITI_COLOR = "#2f6fd0";
const FINE_ARTS_COLOR = "#e8590c";
const INSGESAMT_COLOR = "#495057";
// Hochrechnungs-Balken bleiben in derselben Farbe, aber deutlich heller -
// zusätzlich zur Beschriftung "HR" auf der Achse (Empfehlung: HR muss klar
// als Schätzwert erkennbar sein, nicht wie ein weiteres Ist-Jahr wirken).
const HR_OPACITY = 0.45;

function istHrEintrag(year) {
    return String(year).toUpperCase() === "HR";
}

/**
 * Gruppiert aufeinanderfolgende Zeilen mit derselben Artikelart zu einem
 * Block (die Zeilen kommen bereits in Excel-Reihenfolge vom Server, siehe
 * SalesAnalysisQuery::graffitiFineArts()) - Grundlage für die Kleinserie an
 * gestapelten Säulendiagrammen (Diagramm 1 aus der Excel-Vorlage).
 */
function gruppiereNachArtikelart(rows) {
    const gruppen = [];
    rows.forEach((row) => {
        const letzte = gruppen[gruppen.length - 1];
        if (letzte && letzte.article_type === row.article_type) {
            letzte.items.push(row);
        } else {
            gruppen.push({article_type: row.article_type, items: [row]});
        }
    });
    return gruppen;
}

/**
 * Entfernt das angefangene laufende Jahr aus den Diagrammdaten (wie in der
 * Excel-Vorlage: nur die vollen Jahre plus "HR"). Das Teiljahr bleibt in
 * der Datentabelle darunter unverändert sichtbar - hier geht es nur um die
 * Diagramme, sonst stünde zwischen dem letzten vollen Jahr und "HR" eine
 * unbeschriftete, nicht aussagekräftige Säule fürs Teiljahr.
 */
function ohneLaufendesTeiljahr(items, currentYear) {
    return items.filter((item) => istHrEintrag(item.year) || Number(item.year) !== Number(currentYear));
}

/**
 * Graffiti vs. Fine Arts: Artikelart × Jahr je Branche (inkl. Hochrechnungs-
 * zeile), darunter die Jahressumme je Branche und insgesamt. Bildet die
 * beiden Excel-Diagramme "Graffiti vs. Fine Arts" nach (befund.md,
 * Diagramme 1 und 2): gestapelte Säulen je Artikelart und Jahr, dazu
 * Jahressummen als gruppierte Säulen mit der Linie "Insgesamt". Tabellen
 * bleiben darunter, wie die Datentabelle in der Excel-Vorlage.
 */
export default function GraffitiFineArtsView({params, meta}) {
    const {data, loading, error} = useAnalysisData("/vertrieb/daten/graffiti-fine-arts", params);
    const labels = referenceLabels(data?.reference, meta);
    // Ist das Bezugsjahr abgeschlossen, gibt es keine Hochrechnung mehr
    // (schnittstelle.md 7.2) - die HR-Zeile/-Spalte der Excel-Vorlage
    // entfällt dann, statt eine bedeutungslose Wiederholung des Bezugsjahres
    // zu zeigen.
    const showHr = labels.hr !== null;
    const rows = (data?.rows || []).filter((row) => showHr || !istHrEintrag(row.year));
    const totals = (data?.totals || []).filter((row) => showHr || !istHrEintrag(row.year));
    const currentLabel = currentYearLabel(meta);

    // Das laufende Jahr ist nur bis zum Stichmonat befüllt - die
    // Jahreszeile bekommt deshalb dieselbe dynamische Beschriftung wie in
    // den übrigen Sichten ("2026 bis August" statt nur "2026", Befund E).
    const jahresLabel = (jahr) => {
        if (istHrEintrag(jahr)) {
            return "HR";
        }
        return meta?.current_year && Number(jahr) === Number(meta.current_year) ? currentLabel : jahr;
    };

    const artikelartGruppen = gruppiereNachArtikelart(rows);
    const totalsOhneTeiljahr = ohneLaufendesTeiljahr(totals, meta?.current_year);
    const totalsChart = totalsOhneTeiljahr.map((row, index, alle) => {
        const vorletzteIstHr = index === alle.length - 2 && istHrEintrag(alle[alle.length - 1]?.year);
        return {
            ...row,
            yearLabel: jahresLabel(row.year),
            // "Insgesamt" wird in zwei Linien zerlegt: durchgezogen für die
            // Ist-Jahre, gestrichelt für den letzten Abschnitt zur
            // Hochrechnung - damit die HR nicht wie ein weiteres Ist-Jahr
            // wirkt (Empfehlung aus dem Befund).
            total_ist: istHrEintrag(row.year) ? null : row.total,
            total_hr: istHrEintrag(row.year) || vorletzteIstHr ? row.total : null,
        };
    });

    return (
        <div className="d-flex flex-column gap-3">
            <AnalysisCard
                title="Graffiti vs. Fine Arts – nach Artikelart"
                help="Umsatz je Artikelart und Jahr, gestapelt aus Graffiti- und Fine-Arts-Kunden. Die Hochrechnung (HR) erscheint heller."
                loading={loading}
                error={error}
                empty={!loading && rows.length === 0}
                height={260}
            >
                <div className="d-flex align-items-center gap-3 small mb-2">
                    <span className="d-inline-flex align-items-center gap-1">
                        <span style={{display: "inline-block", width: 10, height: 10, backgroundColor: GRAFFITI_COLOR, borderRadius: 2}} />
                        Graffiti
                    </span>
                    <span className="d-inline-flex align-items-center gap-1">
                        <span style={{display: "inline-block", width: 10, height: 10, backgroundColor: FINE_ARTS_COLOR, borderRadius: 2}} />
                        Fine Art
                    </span>
                </div>
                <div className="row g-3 mb-3">
                    {artikelartGruppen.map((gruppe) => {
                        const chartItems = ohneLaufendesTeiljahr(gruppe.items, meta?.current_year);
                        return (
                            <div className="col" key={gruppe.article_type} style={{minWidth: 180}}>
                                <div className="small text-muted text-center mb-1">{gruppe.article_type}</div>
                                <ResponsiveContainer width="100%" height={200}>
                                    <BarChart data={chartItems} margin={{top: 4, right: 4, left: 4, bottom: 4}}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="year" tickFormatter={jahresLabel} tick={{fontSize: 11}} axisLine={false} tickLine={false} />
                                        <YAxis tickFormatter={formatEuroCompact} tick={{fontSize: 11}} width={56} axisLine={false} tickLine={false} />
                                        <Tooltip formatter={(value, name) => [formatEuro(value), name]} labelFormatter={jahresLabel} />
                                        <Bar dataKey="graffiti" name="Graffiti" stackId="branche" fill={GRAFFITI_COLOR} isAnimationActive={false}>
                                            {chartItems.map((item) => (
                                                <Cell key={item.year} fillOpacity={istHrEintrag(item.year) ? HR_OPACITY : 1} />
                                            ))}
                                        </Bar>
                                        <Bar dataKey="fine_arts" name="Fine Art" stackId="branche" fill={FINE_ARTS_COLOR} isAnimationActive={false}>
                                            {chartItems.map((item) => (
                                                <Cell key={item.year} fillOpacity={istHrEintrag(item.year) ? HR_OPACITY : 1} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        );
                    })}
                </div>
                <div className="table-responsive">
                    <Table size="sm" hover className="mb-0 align-middle">
                        <thead>
                            <tr>
                                <th>Type / Artikelart</th>
                                <th>Jahr</th>
                                <th className="text-end">Graffiti</th>
                                <th className="text-end">Fine Art</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, index) => {
                                const isHrRow = istHrEintrag(row.year);
                                return (
                                    <tr key={`${row.article_type}-${row.year}-${index}`} className={isHrRow ? "fw-semibold table-light" : undefined}>
                                        <td>{row.article_type}</td>
                                        <td>{jahresLabel(row.year)}</td>
                                        <td className="text-end text-nowrap">{formatEuroWhole(row.graffiti)}</td>
                                        <td className="text-end text-nowrap">{formatEuroWhole(row.fine_arts)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>
            </AnalysisCard>

            <AnalysisCard
                title="Graffiti vs. Fine Arts – Jahressumme"
                help="Jahressumme je Branche, gruppiert, mit der Linie Insgesamt. Die Hochrechnung (HR) erscheint heller und gestrichelt."
                loading={loading}
                error={error}
                empty={!loading && totals.length === 0}
                height={Math.max(240, totals.length * 40)}
            >
                <div className="row g-3">
                    <div className="col-lg-6">
                        <div className="table-responsive">
                            <Table size="sm" hover className="mb-0 align-middle">
                                <thead>
                                    <tr>
                                        <th>Jahr</th>
                                        <th className="text-end">Graffiti</th>
                                        <th className="text-end">Fine Art</th>
                                        <th className="text-end">Insgesamt</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {totals.map((row) => (
                                        <tr key={row.year}>
                                            <td>{jahresLabel(row.year)}</td>
                                            <td className="text-end text-nowrap">{formatEuroWhole(row.graffiti)}</td>
                                            <td className="text-end text-nowrap">{formatEuroWhole(row.fine_arts)}</td>
                                            <td className="text-end text-nowrap fw-semibold">{formatEuroWhole(row.total)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <ResponsiveContainer width="100%" height={Math.max(220, totals.length * 40)}>
                            {/* ComposedChart statt BarChart: BarChart sammelt Achsen-
                                domäne und Legende NUR aus <Bar>-Kindern
                                (generateCategoricalChart, GraphicalChild: Bar) - die
                                Linie "Insgesamt" wäre damit weder in der Domäne noch in
                                der Legende berücksichtigt worden (Sichtprüfung
                                14.09.2026: Achse endete bei ~8 statt ~11,5 Mio. €,
                                Legende zeigte nur Graffiti/Fine Art). ComposedChart
                                erkennt Line, Area, Bar und Scatter gleichermaßen. */}
                            <ComposedChart data={totalsChart} margin={{top: 8, right: 16, left: 8, bottom: 8}}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="year" tickFormatter={jahresLabel} tick={{fontSize: 12}} />
                                <YAxis tickFormatter={formatEuroCompact} tick={{fontSize: 12}} width={90} />
                                <Tooltip formatter={(value, name) => [formatEuro(value), name]} labelFormatter={jahresLabel} />
                                <Legend />
                                <Bar dataKey="graffiti" name="Graffiti" fill={GRAFFITI_COLOR} radius={[4, 4, 0, 0]} isAnimationActive={false}>
                                    {totalsChart.map((row) => (
                                        <Cell key={row.year} fillOpacity={istHrEintrag(row.year) ? HR_OPACITY : 1} />
                                    ))}
                                </Bar>
                                <Bar dataKey="fine_arts" name="Fine Art" fill={FINE_ARTS_COLOR} radius={[4, 4, 0, 0]} isAnimationActive={false}>
                                    {totalsChart.map((row) => (
                                        <Cell key={row.year} fillOpacity={istHrEintrag(row.year) ? HR_OPACITY : 1} />
                                    ))}
                                </Bar>
                                <Line type="monotone" dataKey="total_ist" name="Insgesamt" stroke={INSGESAMT_COLOR} strokeWidth={2} dot={{r: 4}} connectNulls={false} isAnimationActive={false} />
                                <Line type="monotone" dataKey="total_hr" name="Insgesamt (HR)" stroke={INSGESAMT_COLOR} strokeWidth={2} strokeDasharray="6 4" dot={{r: 4}} connectNulls={false} isAnimationActive={false} legendType="none" />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </AnalysisCard>
        </div>
    );
}
